/**
 * BrowseCivitAILoRAsModal
 *
 * Searchable modal for browsing and installing LoRAs from CivitAI.
 * Follows the dark-theme modal scaffold pattern (ConfirmDialog, etc.).
 *
 * Dark theme only (RULE [[CSS Architecture]]).
 * Mobile-first responsive (RULE #13).
 * RULE #16: getApiBaseUrl + httpUtils.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import {
  type CivitAILoraSearchItem,
  type LoraInstallJobStatus,
  getLoraInstallStatus,
  installLora,
  searchCivitAILoras,
} from '@/services/loraApi';

// ============================================================================
// Types
// ============================================================================

export interface BrowseCivitAILoRAsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the installed LoRA filename once installation completes. */
  onInstalled: (filename: string, recommendedStrength: number) => void;
  /** Constrain CivitAI search to this base model. */
  baseModel: 'SD 1.5' | 'SD 1.5 Hyper' | 'SDXL 1.0';
}

type InstallState =
  | { phase: 'idle' }
  | { phase: 'installing'; jobId: string; status: LoraInstallJobStatus }
  | { phase: 'done'; filename: string }
  | { phase: 'error'; message: string };

// ============================================================================
// Helpers
// ============================================================================

function civitBaseModelParam(base: 'SD 1.5' | 'SD 1.5 Hyper' | 'SDXL 1.0'): string {
  if (base === 'SDXL 1.0') return 'SDXL 1.0';
  return 'SD 1.5';
}

function progressPercent(status: LoraInstallJobStatus): number {
  if (status.total_mb <= 0) return 0;
  return Math.round((status.progress_mb / status.total_mb) * 100);
}

// ============================================================================
// Sub-components
// ============================================================================

function LoraCard({
  item,
  installing,
  done,
  onInstall,
}: {
  item: CivitAILoraSearchItem;
  installing: boolean;
  done: boolean;
  onInstall: (item: CivitAILoraSearchItem) => void;
}): React.ReactElement {
  const { t } = useTranslation();

  return (
    <div className="bg-black/30 border border-white/10 rounded-lg overflow-hidden flex flex-col">
      {item.thumbnail_url ? (
        <img
          src={item.thumbnail_url}
          alt={item.name}
          className="w-full h-32 object-cover"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-32 bg-black/50 flex items-center justify-center text-white/20 text-xs">
          No preview
        </div>
      )}
      <div className="p-3 flex flex-col gap-1.5 flex-1">
        <p className="text-white font-medium text-sm leading-tight line-clamp-2">{item.name}</p>
        <p className="text-white/50 text-xs">{item.creator}</p>
        {item.nsfw && (
          <span className="self-start text-[10px] bg-red-900/40 border border-red-500/30 text-red-300 px-1.5 py-0.5 rounded">
            NSFW
          </span>
        )}
        {item.trigger_words.length > 0 && (
          <p className="text-white/40 text-[10px] truncate">
            {item.trigger_words.slice(0, 3).join(', ')}
          </p>
        )}
        <div className="mt-auto pt-2">
          {done ? (
            <span className="text-xs text-green-400">
              {t('deforum.advanced.civitAILoraModal.installed')}
            </span>
          ) : (
            <button
              type="button"
              disabled={installing}
              onClick={() => onInstall(item)}
              className={`
                w-full min-h-[36px] text-xs font-medium rounded-lg border transition-colors
                ${
                  installing
                    ? 'bg-purple-700/20 border-purple-400/30 text-purple-300 cursor-wait'
                    : 'bg-purple-700/30 border-purple-400/50 text-white hover:bg-purple-700/50'
                }
                disabled:opacity-60 disabled:cursor-not-allowed
              `}
            >
              {installing ? '...' : t('deforum.advanced.civitAILoraModal.installButton')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main Modal
// ============================================================================

export function BrowseCivitAILoRAsModal({
  isOpen,
  onClose,
  onInstalled,
  baseModel,
}: BrowseCivitAILoRAsModalProps): React.ReactElement | null {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CivitAILoraSearchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [installStates, setInstallStates] = useState<Record<number, InstallState>>({});
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollIntervals = useRef<Record<string, ReturnType<typeof setInterval>>>({});

  const doSearch = useCallback(
    async (q: string) => {
      setLoading(true);
      setError(null);
      try {
        const res = await searchCivitAILoras({
          query: q || undefined,
          base_model: civitBaseModelParam(baseModel),
          nsfw: false,
        });
        setResults(res.items);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Search failed');
      } finally {
        setLoading(false);
      }
    },
    [baseModel]
  );

  // Initial search on open
  useEffect(() => {
    if (isOpen) {
      void doSearch('');
    }
  }, [isOpen, doSearch]);

  // Debounced search on query change
  useEffect(() => {
    if (!isOpen) return;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      void doSearch(query);
    }, 400);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [query, isOpen, doSearch]);

  // Cleanup polls on unmount
  useEffect(() => {
    const intervals = pollIntervals.current;
    return () => {
      Object.values(intervals).forEach(clearInterval);
    };
  }, []);

  const startPoll = useCallback(
    (itemId: number, jobId: string) => {
      const interval = setInterval(async () => {
        try {
          const status = await getLoraInstallStatus(jobId);
          setInstallStates((prev) => ({
            ...prev,
            [itemId]: { phase: 'installing', jobId, status },
          }));
          if (status.status === 'installed') {
            clearInterval(interval);

            delete pollIntervals.current[jobId];
            setInstallStates((prev) => ({
              ...prev,
              [itemId]: { phase: 'done', filename: jobId },
            }));
            // We don't have the exact filename from status; pass job_id as fallback.
            // Caller (LoraStack) will refresh installedLoras list to get proper filename.
            onInstalled(jobId, 0.65);
          } else if (status.status === 'failed') {
            clearInterval(interval);

            delete pollIntervals.current[jobId];
            setInstallStates((prev) => ({
              ...prev,
              [itemId]: { phase: 'error', message: status.error ?? 'Install failed' },
            }));
          }
        } catch {
          // transient error, keep polling
        }
      }, 2000);
      pollIntervals.current[jobId] = interval;
    },
    [onInstalled]
  );

  const handleInstall = useCallback(
    async (item: CivitAILoraSearchItem) => {
      setInstallStates((prev) => ({
        ...prev,
        [item.id]: {
          phase: 'installing',
          jobId: '',
          status: { status: 'queued', progress_mb: 0, total_mb: 0, error: null },
        },
      }));
      try {
        const { job_id } = await installLora({ civitai_version_id: item.latest_version_id });
        setInstallStates((prev) => ({
          ...prev,
          [item.id]: {
            phase: 'installing',
            jobId: job_id,
            status: { status: 'queued', progress_mb: 0, total_mb: 0, error: null },
          },
        }));
        startPoll(item.id, job_id);
      } catch (err) {
        setInstallStates((prev) => ({
          ...prev,
          [item.id]: {
            phase: 'error',
            message: err instanceof Error ? err.message : 'Install failed',
          },
        }));
      }
    },
    [startPoll]
  );

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-gray-900 border border-white/10 rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 flex-shrink-0">
          <h2 className="text-white font-semibold text-base">
            {t('deforum.advanced.civitAILoraModal.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-white/50 hover:text-white transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-white/5"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-white/10 flex-shrink-0">
          <input
            type="text"
            className="input bg-black/20 text-white border-white/10 w-full min-h-[44px]"
            placeholder={t('deforum.advanced.civitAILoraModal.searchPlaceholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="flex items-center justify-center py-12 text-white/40 text-sm">
              Loading...
            </div>
          )}
          {!loading && error && (
            <div className="text-red-400 text-sm p-3 bg-red-900/20 rounded-lg border border-red-500/20">
              {error}
            </div>
          )}
          {!loading && !error && results.length === 0 && (
            <div className="text-white/40 text-sm text-center py-12">No results</div>
          )}
          {!loading && results.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {results.map((item) => {
                const state = installStates[item.id] ?? { phase: 'idle' };
                const installing = state.phase === 'installing';
                const done = state.phase === 'done';
                const installError = state.phase === 'error' ? state.message : null;

                return (
                  <div key={item.id}>
                    <LoraCard
                      item={item}
                      installing={installing}
                      done={done}
                      onInstall={handleInstall}
                    />
                    {installing && state.phase === 'installing' && state.status && (
                      <p className="text-xs text-purple-300 mt-1 text-center">
                        {t('deforum.advanced.civitAILoraModal.installing').replace(
                          '{{percent}}',
                          String(progressPercent(state.status))
                        )}
                      </p>
                    )}
                    {installError && (
                      <p className="text-xs text-red-400 mt-1 text-center">
                        {t('deforum.advanced.civitAILoraModal.installFailed').replace(
                          '{{error}}',
                          installError
                        )}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BrowseCivitAILoRAsModal;
